import { _ } from "../../../core/polyglot/polyglot.js";
import { addMonths } from "../../../core/helpers/date-util.js";
import { formatFullNoDecimals } from "../../../core/helpers/currency-formatting-util.js";
import AbstractRuleModel from "./AbstractRuleModel.js";
import ManualJournalEntryTransaction from "../ManualJournalEntryTransaction.js";

/**
 * Rule model for a manual journal entry. It consists of a name, and a balanced
 * amount (which sum of credits and debits equals 0) spread over multiple
 * accounts.
 *
 * @param {string} name - Name of the entry
 * @param {ManualJournalEntryTransaction[]} [transactions] - List of account spread defining
 *   how the cash is spread while balanced
 * @param {number} [numberOfRollingMonths] - Positive number
 *   representing the number of months that this rule applies to. 1
 *   means just one time, and Infinity is used for infinite rolling
 */
export default class ManualJournalEntryRuleModel extends AbstractRuleModel {
	constructor({ name, transactions = [], numberOfRollingMonths = 1 } = {}) {
		super(...arguments);

		this._name = name;
		this._transactions = transactions;
		this._numberOfRollingMonths = numberOfRollingMonths;
	}

	getName() {
		return this._name;
	}

	setName(name) {
		this._name = name;
	}

	getTransactions() {
		return this._transactions;
	}

	setTransactions(transactions) {
		this._transactions = transactions;
	}

	getNumberOfRollingMonths() {
		return this._numberOfRollingMonths;
	}

	setNumberOfRollingMonths(numberOfRollingMonths) {
		this._numberOfRollingMonths = numberOfRollingMonths;
	}

	isManualJournalEntry() {
		return true;
	}

	accept(visitor) {
		return visitor.visitManualJournalEntryRuleModel(this);
	}

	/**
	 * Return true if and only if both statement are true:
	 *
	 * 1. `monthIndex` is within the interval starting at
	 * `ruleStartIndex` and ending after the amount of rolling months;
	 *
	 * 2. the receiver has a transaction for `accountNumber`.
	 */
	matchesManualJournalEntry({ accountNumber, monthPeriod, ruleStartPeriod }) {
		if (!this.hasTransactionForAccountNumber(accountNumber)) {
			return false;
		}

		const ruleStartDate = ruleStartPeriod.asDate();

		const ruleStartTime = ruleStartDate.getTime();
		const monthTime = monthPeriod.asDate().getTime();

		if (ruleStartTime > monthTime) {
			return false;
		}

		if (this.hasInfinitelyRollingMonths()) {
			return true;
		}

		const ruleEndTime = addMonths(
			ruleStartDate,
			this.getNumberOfRollingMonths(),
		).getTime();

		return ruleEndTime >= monthTime;
	}

	hasInfinitelyRollingMonths() {
		return this.getNumberOfRollingMonths() === Infinity;
	}

	/**
	 * We need to override this method because otherwise new instances of
	 * Account are created, leading to some mismatch.
	 */
	clone() {
		return new this.constructor({
			name: this._name,
			transactions: this._transactions.map((transaction) =>
				transaction.clone(),
			),
			numberOfRollingMonths: this._numberOfRollingMonths,
		});
	}

	apply(otherManualJournalEntryRuleModel) {
		this._name = otherManualJournalEntryRuleModel.getName();
		this._transactions = otherManualJournalEntryRuleModel.getTransactions();

		this._numberOfRollingMonths =
			otherManualJournalEntryRuleModel.getNumberOfRollingMonths();
	}

	addBalancedTransaction({ account }) {
		const transaction = new ManualJournalEntryTransaction({
			account,
			value: -this.computeCurrentBalance(),
		});

		this._transactions.push(transaction);
	}

	removeTransaction(transaction) {
		const index = this._transactions.findIndex(
			(each) => each === transaction,
		);

		this._transactions.splice(index, 1);
	}

	validate({ collector }) {
		const balance = this.computeCurrentBalance();

		collector.addErrorIf(
			balance !== 0,
			_("The journal must balance. {explanation}", {
				explanation:
					balance > 0
						? _("Out by debit of {balance}", {
								balance: formatFullNoDecimals(balance),
							})
						: _("Out by credit of {balance}", {
								balance: formatFullNoDecimals(-balance),
							}),
			}),
		);

		collector.addErrorIf(
			this._transactions.some(
				(transaction) =>
					transaction.getValue() !== 0 && !transaction.getAccount(),
			),
			_("Every transaction should have an account selected"),
		);

		collector.addErrorIf(
			this._transactions.every(
				(transaction) => transaction.getValue() === 0,
			),
			_("At least one transaction should have a value"),
		);
	}

	computeCurrentBalance() {
		return this._transactions.reduce(
			(result, transaction) => result + transaction.getValue(),
			0,
		);
	}

	hasTransactionForAccountNumber(accountNumber) {
		return !!this.getTransactionForAccountNumber(accountNumber);
	}

	getTransactionForAccountNumber(accountNumber) {
		return this._transactions.find((transaction) =>
			transaction.isForAccountNumber(accountNumber),
		);
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.name = this._name;

		properties.transactions = this._transactions.map((transaction) =>
			transaction.serialize(),
		);

		properties.numberOfRollingMonths = this.hasInfinitelyRollingMonths()
			? null
			: this._numberOfRollingMonths;

		return properties;
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(...arguments);

		data.transactions = ManualJournalEntryTransaction.materializeAll(
			data.transactions,
			context,
		);

		if (data.numberOfRollingMonths === null)
			data.numberOfRollingMonths = Infinity;
	}

	static getLabel() {
		return _("Manual Journal Entry");
	}

	static getDescriptionText() {
		return "";
	}

	static getType() {
		return "ManualJournalEntry";
	}

	static rendererForRuleModels(widget) {
		return widget.rendererForManualJournalEntry(this);
	}
}
